// A switch's power-supply, fan and temperature health, read over its console
// (ZENITH.md §11.2, ADR-0446). An operator asks for it here; the reading lives
// in this panel's state until the next read or the page goes, and is never
// stored — a cached copy of a physical fact can be wrong while looking
// authoritative.
//
// **A redundant supply down is redundancy lost, and never the whole switch.**
// The main supply's status is the switch's power (ADR-0446 decision 3); a
// redundant supply reading down means the switch has no redundancy, said as a
// warning, not as the switch being off. A single-supply switch shows one supply
// and no redundant line, which is not a fault (decision 2).
import { useState } from "react";
import { api } from "../../api/client";
import { oneConsole } from "../../api/oneConsole";
import { useMutation } from "../../api/mutation";
import type { components } from "../../api/schema";
import { ProblemBox } from "../../ui";
import type { Device } from "./commands";

export type HealthReading = components["schemas"]["SwitchHealthReading"];

const clock = (d: Date) => d.toLocaleTimeString();

/** Whether the read shows a redundant supply that is not up: the switch is still
 * powered by its main supply but has lost its redundancy. Exported so a test can
 * pin that it is said for a down redundant supply and not for a single-supply
 * switch. */
export function redundancyLost(reading: HealthReading): boolean {
  return reading.supplies.some((s) => s.name === "redundant" && !s.healthy);
}

export function SwitchHealth({ device }: { device: Device }) {
  const [reading, setReading] = useState<HealthReading>();
  const lane = useMutation();
  const read = async () => {
    const r = await lane.runWith(() => oneConsole(device.serial, "health:read", () => api.POST("/switches/{serial}/health:read", { params: { path: { serial: device.serial } } })));
    if (r.ok && r.data) setReading(r.data);
  };
  return (
    <section className="panel">
      <h3>Power, fans and temperature</h3>
      {/* Restates ADR-0446 and ADR-0115 decision 4 — one show line, nothing kept, refused while a console, a read or a PoE cycle is open on the switch. */}
      <div className="actions">
        <button type="button" className="btn" onClick={() => void read()} disabled={lane.busy}
          title="Types show system at the switch's console and shows each power supply, fan and temperature, with the time of the read. Nothing is kept or sent to the switch. It is refused while a console, a read or a PoE cycle is open on the switch.">
          {lane.busy ? "Reading health from the console…" : reading ? "Read health from the console again" : "Read health from the console"}
        </button>
      </div>
      {lane.problem && <ProblemBox problem={lane.problem} onDismiss={lane.clear} />}
      {reading && <HealthReadingView reading={reading} />}
    </section>
  );
}

function HealthReadingView({ reading }: { reading: HealthReading }) {
  const readAt = new Date(reading.read_at);
  return (
    <div>
      <div>
        <strong>Power supplies</strong>
        {redundancyLost(reading) && (
          <span className="warn" title="A redundant power supply is not up. The switch is still powered by its main supply, but it has lost its redundancy."> · redundancy lost</span>
        )}
        <ul>
          {reading.supplies.map((s) => (
            <li key={s.name} className={s.healthy ? undefined : "warn"}>
              {s.name === "main" ? "Main" : "Redundant"}: {s.healthy ? "up" : "down"}
            </li>
          ))}
        </ul>
      </div>
      {reading.fans.length > 0 && (
        <div>
          <strong>Fans</strong>
          <ul>
            {reading.fans.map((f) => (
              <li key={f.name} className={f.ok ? undefined : "warn"}>
                {f.name}: {f.status}{f.speed_rpm != null ? ` · ${f.speed_rpm} rpm` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}
      {reading.temperatures.length > 0 && (
        <div>
          <strong>Temperature</strong>
          <ul>
            {reading.temperatures.map((t) => (
              <li key={t.sensor}>
                {t.sensor}: {t.degrees_c}&deg;C{t.rising_c != null ? ` · rising ${t.rising_c}°C` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="muted">Read over the switch&apos;s console with show system &middot; {clock(readAt)}</div>
    </div>
  );
}
